import { useEffect, useState } from "react";
import { useTranslation as useI18nTranslation } from "react-i18next";
import { useAllTranslations } from "./useTranslations";

interface TranslationLoadingState {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  ready: boolean;
}

export const useTranslationLoader = (language: string = "en"): TranslationLoadingState => {
  const [ready, setReady] = useState(false);
  const { i18n: i18nInstance } = useI18nTranslation();

  const {
    data: translations,
    isLoading,
    isError,
    error,
    isSuccess,
  } = useAllTranslations(language);

  useEffect(() => {
    if (isSuccess && translations && i18nInstance) {
      Object.entries(translations).forEach(([namespace, translationData]) => {
        i18nInstance.addResourceBundle(language, namespace, translationData, true, true);
      });

      if (i18nInstance.language !== language) {
        i18nInstance.changeLanguage(language).then(() => {
          setReady(true);
        }).catch((err) => {
          console.error("Failed to change language:", err);
          setReady(true); // Still mark as ready to prevent blocking
        });
      } else {
        setReady(true);
      }
    }
  }, [isSuccess, translations, language, i18nInstance]);

  useEffect(() => {
    setReady(false);
  }, [language]);

  useEffect(() => {
    const handleLanguageChanged = () => {
      if (i18nInstance.isInitialized && !isLoading && !isError) {
        setReady(true);
      }
    };

    i18nInstance.on('languageChanged', handleLanguageChanged);
    i18nInstance.on('loaded', handleLanguageChanged);
    i18nInstance.on('initialized', handleLanguageChanged);

    return () => {
      i18nInstance.off('languageChanged', handleLanguageChanged);
      i18nInstance.off('loaded', handleLanguageChanged);
      i18nInstance.off('initialized', handleLanguageChanged);
    };
  }, [i18nInstance, isLoading, isError]);

  return {
    isLoading,
    isError,
    error: error as Error | null,
    ready: ready && !isLoading && !isError,
  };
};


export const useLanguageSwitcher = () => {
  const { i18n: i18nInstance } = useI18nTranslation();

  const switchLanguage = async (newLanguage: string) => {
    try {
      await i18nInstance.changeLanguage(newLanguage);
    } catch (error) {
      console.error(`Failed to switch language to ${newLanguage}:`, error);
      throw error;
    }
  };

  return {
    currentLanguage: i18nInstance.language,
    switchLanguage,
  };
};
